import { formatDate, timeAgo } from '@/helpers/times'
import { plainText } from '@/helpers/markdown'
import { normalizeText, searchTerms } from '@/helpers/text'
import { HOME_GUIDE_COVERS } from '@/constants/home'

// A CMS media path, which is relative to the CMS host; an absolute URL passes through.
export const cmsUrl = (path) => new URL(path, import.meta.env.VITE_STRAPI_API_HOST).href

// A CMS picture at `format` when it has that rendition, else the original.
export const cmsImageUrl = (image, format) => cmsUrl(image.formats?.[format]?.url ?? image.url)

// A post's cover for a card: the CMS's rendition at `format` when it has one.
export const postCoverUrl = (post, format = 'small') => {
  const cover = post.attributes.coverImage?.data?.attributes
  return cover ? cmsImageUrl(cover, format) : null
}

// A picture taller than twice its width (a long table) keeps its full height; capping it would leave a sliver.
export const isTallImage = ({ attributes: { width, height } }) => height > width * 2

// The CMS caps every rendition on its longest side, which leaves a tall picture a few pixels wide: those load the original.
export const sectionImageUrl = (image) => cmsImageUrl(image.attributes, isTallImage(image) ? undefined : 'large')

// Whether the post is a video, which its thumbnail labels "Watch".
export const postHasVideo = ({ attributes }) => Boolean(attributes.youtubeLink || attributes.coverVideo?.data)

// The blurb as plain words for a card, since editors write bold into it.
export const postBlurb = (post) => plainText(post.attributes.introBlurb)

// "<author> • <time ago>", from whichever of the two the post has.
export const postByline = ({ attributes: { author, date } }) =>
  [author, date && timeAgo(date)].filter(Boolean).join(' • ')

// "By <author> · Updated <date>", the article page's attribution line, from whichever of the two the post has.
export const postAttribution = ({ attributes: { author, updatedAt } }) =>
  [author && `By ${author}`, updatedAt && `Updated ${formatDate(updatedAt)}`].filter(Boolean).join(' · ')

const WORDS_PER_MINUTE = 200

// How long the post's sections take to read, in whole minutes, never under one: a video post has little text.
export const postReadingMinutes = (post) => {
  const words = (post.attributes.section ?? []).reduce((count, section) => count + (section.description?.match(/\S+/g)?.length ?? 0), 0)
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE))
}

// A YouTube link that names one video; a channel or playlist cannot be embedded.
const isYoutubeVideo = (url) => /youtu\.be\/|watch\?v=|shorts\//.test(url ?? '')

// The post's own video, above its sections: its file, or its YouTube link; null when it has neither.
export const postVideo = ({ attributes: { coverVideo, youtubeLink } }) => {
  const file = coverVideo?.data?.attributes
  if (file) return { src: cmsUrl(file.url) }
  return isYoutubeVideo(youtubeLink) ? { youtube: youtubeLink } : null
}

// A section's video under its copy: its file with its poster, or its YouTube link; null when it has neither.
export const sectionVideo = ({ video, youtubeLink }) => {
  const file = video?.file?.data?.attributes
  const poster = video?.poster?.data?.attributes
  if (file) return { src: cmsUrl(file.url), poster: poster ? cmsUrl(poster.url) : null }
  return isYoutubeVideo(youtubeLink) ? { youtube: youtubeLink } : null
}

// A callout opens a quoted block with `> [!TIP] A title`: the admonition form, with the title after the marker.
const CALLOUT_OPEN = /^>\s*\[!(\w+)\]\s*(.*)$/
const CALLOUT_TONES = { TIP: 'info', NOTE: 'info', INFO: 'info', IMPORTANT: 'warning', WARNING: 'warning', CAUTION: 'warning' }

// Splits Markdown into runs of Markdown and callouts `{ tone, title, body }`, in order; a quote without the marker stays Markdown.
export const splitArticleBlocks = (markdown) => {
  const blocks = []
  const lines = (markdown ?? '').split(/\r?\n/)
  let run = []
  let index = 0

  const flush = () => {
    const source = run.join('\n')
    if (source.trim()) blocks.push({ type: 'markdown', source })
    run = []
  }

  while (index < lines.length) {
    const match = lines[index].match(CALLOUT_OPEN)
    const tone = match && CALLOUT_TONES[match[1].toUpperCase()]

    if (!tone) {
      run.push(lines[index])
      index = index + 1
      continue
    }

    const body = []
    index = index + 1
    while (index < lines.length && lines[index].startsWith('>')) {
      body.push(lines[index].replace(/^>\s?/, ''))
      index = index + 1
    }

    flush()
    const type = match[1].toLowerCase()
    blocks.push({ type: 'callout', tone, title: match[2].trim() || type.charAt(0).toUpperCase() + type.slice(1), body: body.join('\n').trim() })
  }

  flush()
  return blocks
}

/**
 * A post's tags, in CMS order: `{ id, title, slug, color }` each. A post may carry none, and
 * the relation is many-to-many — a post with two tags shows two badges.
 *
 * `color` is a badge variant already (the CMS enumeration is the `AppBadge` variant list), so
 * nothing maps names to colours any more: a tag added in Strapi arrives with its own colour
 * and needs no deploy. `gray` is the fallback for a tag saved before the field was required.
 */
export const postTags = (post) => (post?.attributes?.tags?.data ?? []).map((tag) => ({
  id: tag.id,
  title: tag.attributes.title,
  slug: tag.attributes.slug,
  color: tag.attributes.color || 'gray',
}))

// The tag that also lifts a hub post into Home's Guides row. A guide is an ordinary CDP Hub
// post — it stays in the hub's lists; the tag is only what gives it a second home.
export const GUIDES_TAG_SLUG = 'guides'

export const postHasTag = (post, slug) => postTags(post).some((tag) => tag.slug === slug)

// A guide's drawn cover, matched to the post by title; null for a post we have no art for,
// which then falls back to its own picture. Title-keyed because the artwork lives in the
// frontend while the posts live in the CMS — a renamed post quietly loses its art, which is
// the price of that split until the cover spec moves into Strapi.
export const postGuideCover = (post) => HOME_GUIDE_COVERS[post?.attributes?.title] ?? null

// The hub's posts tagged for Home's Guides row, newest first — `feeds` is already in that order.
export const guidePosts = (posts) => posts.filter((post) => postHasTag(post, GUIDES_TAG_SLUG))

// The feed without them, which is what the legacy Home's hub list showed before the guides
// became CMS posts. The new design lists a guide in the hub like any other post; only the
// legacy list is held to what it had, and this goes away with it.
export const postsWithoutGuides = (posts) => posts.filter((post) => !postHasTag(post, GUIDES_TAG_SLUG))

// The words the hub's search can meet: the title, the blurb, the author and the tags; the body would match too broadly.
const postSearchText = (post) =>
  normalizeText([
    post.attributes.title,
    plainText(post.attributes.introBlurb),
    post.attributes.author,
    ...postTags(post).map((tag) => tag.title),
  ].filter(Boolean).join(' '))

// Whether a post holds every word searched for and, with categories chosen (a Set of tag slugs), carries ONE of them — a post tagged both "Guides" and "News" belongs under either.
export const postMatches = (post, { terms, categories }) => {
  if (categories && !postTags(post).some((tag) => categories.has(tag.slug))) return false
  if (!terms.length) return true
  const text = postSearchText(post)
  return terms.every((term) => text.includes(term))
}

// The list's filter from the search box and the chosen categories: every category, or none, is no filter, as the filter's own label reads it.
export const hubFilter = (query, chosen, options) => {
  const terms = searchTerms(query)
  const categories = chosen.length && chosen.length < options.length ? new Set(chosen) : null
  return { terms, categories, active: terms.length > 0 || categories !== null }
}
